<template>
  <!-- eslint-disable vue/no-v-html -->
  <div class="user-status" :class="{ 'user-status--form-open': statusFormOpen }">
    <div class="user-status__heading-container">
      <h2
        class="user-status__heading heading--3"
      >Status</h2>
      <time-stamp
        v-if="currentStatus && !statusFormOpen"
        class="user-status__timestamp"
        :date="currentStatus.insertedAt"
        format="dateAndTime"
      />
    </div>
    <div class="user-status__box">
      <p
        v-if="currentStatus && !statusFormOpen"
        class="user-status__current-status"
        data-test-id="user-status-current-status"
        v-html="formattedStatus"
      />
      <p
        v-if="placeholder"
        class="user-status__placeholder"
        @click="openStatusForm"
      >
        <span><slot>How are you feeling today?</slot></span>
        <icon-component name="pencil" class="user-status__new-icon" />
      </p>
      <status-form
        v-if="statusFormOpen"
        @newStatus="updateStatus"
        @statusPending="closeStatusForm"
      />
    </div>
    <status-controls
      v-if="currentStatus && !statusFormOpen"
      :current-status="currentStatus"
      @openStatusForm="openStatusForm"
      @newStatus="updateStatus"
      @showPlaceholder="showPlaceholder"
    />
  </div>
</template>

<script>
import TimeStamp from '@/components/Global/TimeStamp.vue';
import StatusForm from '@/components/User/Statuses/StatusForm.vue';
import StatusControls from '@/components/User/Statuses/StatusControls.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'UserStatus',
  components: {
    TimeStamp,
    StatusForm,
    StatusControls,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
    truncate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentStatus: undefined,
      statusFormOpen: false,
      placeholderVisible: false,
    };
  },
  computed: {
    ...mapGetters([
      'userIsLoggedIn',
      'userIsUnverified',
    ]),
    placeholder() {
      return (!this.currentStatus && !this.statusFormOpen) || this.placeholderVisible;
    },
    formattedStatus() {
      // eslint-disable-next-line no-useless-escape
      const pattern = /(.*)<img/;
      const hasImage = pattern.test(this.currentStatus.body);

      if (!this.currentStatus.body) {
        return '';
      }

      if (hasImage) {
        const statusText = this.currentStatus.body.match(pattern)[1];

        if (this.truncate && statusText.length > 30) {
          const truncatedText = `"${statusText.split(' ').splice(0, 30).join(' ')}..."`;
          return this.currentStatus.body.replace(statusText, truncatedText);
        }

        const newText = `"${statusText}"`;
        return this.currentStatus.body.replace(statusText, newText);
      }

      if (!hasImage && this.truncate && this.currentStatus.body.length > 30) {
        const truncatedText = this.currentStatus.body.split(' ').splice(0, 30).join(' ');
        return `"${truncatedText}..."`;
      }

      return `"${this.currentStatus.body}"`;
    },
  },
  created() {
    this.currentStatus = this.user.profileStatus;
  },
  methods: {
    updateStatus(status) {
      this.currentStatus = status;
      this.statusFormOpen = false;
    },
    closeStatusForm() {
      this.statusFormOpen = false;
    },
    openStatusForm(event) {
      if (!this.userIsLoggedIn) {
        this.$store.dispatch('openRegisterDialog', { dialogText: 'Create an account to post a status.' });
        this.$snowplow.trackButtonEvent({
          data: {
            type: 'status',
            text: event.target.textContent.trim(),
            classes: event.target.classList,
            id: event.target.id,
          },
        });
        return;
      }
      if (this.userIsLoggedIn && this.userIsUnverified) {
        this.$store.dispatch('openVerificationPrompt', { dialogHeading: 'Verify your account' });
        return;
      }
      this.placeholderVisible = false;
      this.statusFormOpen = true;
    },
    showPlaceholder() {
      this.currentStatus = undefined;
      this.placeholderVisible = true;
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '@/stylesheets/components/_user-status';
</style>

<style lang="scss"> // no scoped - to reach class used in status
  @import '@/stylesheets/components/_uploaded-image';

  .user-status__current-status a:focus {
    @include highlighter-focus();
  }
</style>
